import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import AdminNavbar from './AdminNavbar';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';

const { FiBarChart3, FiTrendingUp, FiUsers, FiMonitor, FiDollarSign, FiActivity } = FiIcons;

const AdminAnalytics = () => {
  const platformStatsOption = {
    title: {
      text: 'Platform Usage Statistics',
      textStyle: { color: '#ffffff' }
    },
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: ['Users', 'Monitors', 'Revenue'],
      textStyle: { color: '#ffffff80' }
    },
    xAxis: {
      type: 'category',
      data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      axisLabel: { color: '#ffffff80' }
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#ffffff80' }
    },
    series: [
      {
        name: 'Users',
        data: [820, 932, 901, 934, 1290, 1330],
        type: 'line',
        smooth: true,
        itemStyle: { color: '#8b5cf6' }
      },
      {
        name: 'Monitors',
        data: [1200, 1400, 1350, 1500, 1800, 2100],
        type: 'line',
        smooth: true,
        itemStyle: { color: '#06b6d4' }
      },
      {
        name: 'Revenue',
        data: [65, 72, 68, 75, 82, 89],
        type: 'line',
        smooth: true,
        itemStyle: { color: '#10b981' }
      }
    ]
  };

  const userDistributionOption = {
    title: {
      text: 'User Plan Distribution',
      textStyle: { color: '#ffffff' }
    },
    tooltip: {
      trigger: 'item'
    },
    series: [{
      type: 'pie',
      radius: '70%',
      data: [
        { value: 45, name: 'Free', itemStyle: { color: '#6b7280' } },
        { value: 35, name: 'Professional', itemStyle: { color: '#8b5cf6' } },
        { value: 20, name: 'Enterprise', itemStyle: { color: '#06b6d4' } }
      ],
      label: {
        color: '#ffffff'
      }
    }]
  };

  const revenueBreakdownOption = {
    title: {
      text: 'Revenue by Plan',
      textStyle: { color: '#ffffff' }
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      axisLabel: { color: '#ffffff80' }
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#ffffff80' }
    },
    series: [
      {
        name: 'Professional',
        data: [25000, 28000, 26000, 30000, 35000, 38000],
        type: 'bar',
        stack: 'revenue',
        itemStyle: { color: '#8b5cf6' }
      },
      {
        name: 'Enterprise',
        data: [40000, 44000, 42000, 45000, 47000, 51500],
        type: 'bar',
        stack: 'revenue',
        itemStyle: { color: '#06b6d4' }
      }
    ]
  };

  const topMetrics = [
    {
      title: 'Total Revenue',
      value: '$489,500',
      change: '+15.2%',
      trend: 'up',
      icon: FiDollarSign,
      color: 'from-green-400 to-emerald-400'
    },
    {
      title: 'Active Users',
      value: '1,247',
      change: '+8.1%',
      trend: 'up',
      icon: FiUsers,
      color: 'from-blue-400 to-cyan-400'
    },
    {
      title: 'Connected Monitors',
      value: '3,456',
      change: '+12.3%',
      trend: 'up',
      icon: FiMonitor,
      color: 'from-purple-400 to-pink-400'
    },
    {
      title: 'Platform Uptime',
      value: '99.9%',
      change: '+0.1%',
      trend: 'up',
      icon: FiActivity,
      color: 'from-orange-400 to-red-400'
    }
  ];

  return (
    <div className="min-h-screen">
      <AdminNavbar />
      
      <div className="pt-20 px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-white mb-2">Analytics Dashboard</h1>
            <p className="text-white/70">
              Comprehensive platform analytics and insights
            </p>
          </motion.div>

          {/* Top Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            {topMetrics.map((metric, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${metric.color} rounded-xl flex items-center justify-center`}>
                    <SafeIcon icon={metric.icon} className="text-white text-xl" />
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-white">{metric.value}</p>
                    <div className="flex items-center space-x-1">
                      <SafeIcon 
                        icon={FiTrendingUp} 
                        className={`text-sm ${metric.trend === 'up' ? 'text-green-400' : 'text-red-400'}`} 
                      />
                      <span className={`text-sm ${metric.trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                        {metric.change}
                      </span>
                    </div>
                  </div>
                </div>
                <h3 className="text-white/80 font-medium">{metric.title}</h3>
              </div>
            ))}
          </motion.div>

          {/* Charts Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Platform Usage */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6"
            >
              <ReactECharts 
                option={platformStatsOption} 
                style={{ height: '400px' }}
                theme="dark"
              />
            </motion.div>

            {/* User Distribution */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6"
            >
              <ReactECharts 
                option={userDistributionOption} 
                style={{ height: '400px' }}
                theme="dark"
              />
            </motion.div>
          </div>

          {/* Revenue Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6"
          >
            <ReactECharts 
              option={revenueBreakdownOption} 
              style={{ height: '400px' }}
              theme="dark"
            />
          </motion.div>

          {/* Key Insights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-8 bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6"
          >
            <h2 className="text-xl font-semibold text-white mb-6">Key Insights</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-white font-medium mb-2">Growth Rate</h3>
                <p className="text-white/70 text-sm">
                  User acquisition has increased by 15% this quarter, with Enterprise plans showing the strongest growth.
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-white font-medium mb-2">Revenue Trends</h3>
                <p className="text-white/70 text-sm">
                  Monthly recurring revenue has grown consistently, with Enterprise customers contributing 60% of total revenue.
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-white font-medium mb-2">Platform Health</h3>
                <p className="text-white/70 text-sm">
                  System uptime remains excellent at 99.9%, with average response times under 200ms globally.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;